/**
 * EventGivingStation — the on-site giving surface for a donor event (Phase 6).
 *
 * Two jobs the product had no answer for:
 *
 * 1. A ROOM-FACING screen — a large scannable QR to the public donate page, the
 *    live tote, and a recent-gifts ticker. The QR is rendered by the backend's
 *    existing QR service, so no frontend QR dependency is needed.
 *
 * 2. A STAFF panel that records gifts as they come in. Card-present is shown even
 *    when Stripe Terminal is not configured, with a setup explanation — the "Take
 *    a card" button used to be hidden entirely when `!terminal.ready`, so staff
 *    saw a greyed "Card reader off" chip and no path forward.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, CreditCard, ExternalLink, Loader2, Maximize2, Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DonorEvent, GivingStation, getGivingStation } from '@/services/api/donorEvents';
import { TerminalStatus, createOfflineDonation, getTerminalStatus } from '@/services/api/donations';
import CardReaderPad from '@/components/donors/CardReaderPad';

function money(n?: number | null): string {
  return `$${Math.round(n || 0).toLocaleString('en-US')}`;
}

export const EventGivingStation: React.FC<{
  event: DonorEvent;
  onExit: () => void;
  onChanged: () => void;
}> = ({ event, onExit, onChanged }) => {
  const [station, setStation] = useState<GivingStation | null>(null);
  const [terminal, setTerminal] = useState<TerminalStatus | null>(null);
  const [presenting, setPresenting] = useState(false);
  const [showReader, setShowReader] = useState(false);
  const [amount, setAmount] = useState('');
  const [donor, setDonor] = useState('');
  const [email, setEmail] = useState('');
  const [method, setMethod] = useState('cash');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setStation(await getGivingStation(event.id));
    } catch (e: any) {
      setError(e?.message || 'Could not load the giving station.');
    }
  }, [event.id]);

  useEffect(() => {
    load();
    getTerminalStatus()
      .then(setTerminal)
      .catch(() => setTerminal(null));
  }, [load]);

  // The tote is a live number on a screen in the room — keep it moving.
  useEffect(() => {
    const t = window.setInterval(load, 20000);
    return () => window.clearInterval(t);
  }, [load]);

  const record = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return;
    setBusy(true);
    setMsg(null);
    setError(null);
    try {
      await createOfflineDonation({
        amount: amt,
        method: method as any,
        donor_name: donor.trim() || undefined,
        // Capturing the email here is what makes the gift receiptable at all —
        // in-event gifts used to be recorded with no address and send_receipt:false.
        donor_email: email.trim() || undefined,
        event_id: event.id,
        is_tax_deductible: true,
        send_receipt: !!email.trim(),
      });
      setMsg(
        email.trim()
          ? `Recorded ${money(amt)} and emailed a receipt.`
          : `Recorded ${money(amt)}. No email captured — the receipt is waiting in the receipts queue.`,
      );
      setAmount('');
      setDonor('');
      setEmail('');
      await load();
      onChanged();
    } catch (e: any) {
      setError(e?.message || 'Could not record the gift.');
    } finally {
      setBusy(false);
    }
  };

  const field =
    'w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

  // Room-facing full-screen mode: QR + tote only, legible from a distance.
  if (presenting && station) {
    return (
      <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-white p-8 dark:bg-gray-900">
        <button
          onClick={() => setPresenting(false)}
          className="absolute right-6 top-6 rounded px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          Exit
        </button>
        <h1 className="mb-2 text-center text-4xl font-bold text-gray-900 dark:text-gray-100">{station.event_title}</h1>
        <p className="mb-8 text-xl text-gray-500">Scan to give</p>
        {station.qr_image ? (
          <img src={station.qr_image} alt="Scan to donate" className="h-72 w-72 rounded-lg border-8 border-white shadow-lg" />
        ) : (
          <div className="rounded-lg border-2 border-dashed border-gray-300 p-10 text-center text-gray-400">
            QR unavailable — use the link below
          </div>
        )}
        <p className="mt-6 break-all text-center text-lg text-blue-600">{station.donate_url}</p>
        <div className="mt-10 text-center">
          <div className="text-6xl font-bold text-green-600">{money(station.raised)}</div>
          <div className="mt-1 text-lg text-gray-500">
            raised from {station.gift_count} gift{station.gift_count === 1 ? '' : 's'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button onClick={onExit} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">
          <ArrowLeft size={15} /> Back to event
        </button>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load}>
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setPresenting(true)} disabled={!station}>
            <Maximize2 size={14} className="mr-1" /> Show on screen
          </Button>
        </div>
      </div>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[1fr_1.2fr]">
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 p-4 text-center dark:border-gray-700">
            <div className="text-3xl font-bold text-green-600">{money(station?.raised)}</div>
            <div className="text-xs text-gray-500">
              raised at this event · {station?.gift_count ?? 0} gifts · {station?.attended ?? 0} checked in
            </div>
            {station?.by_method?.length ? (
              <div className="mt-3 flex flex-wrap justify-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-700">
                {station.by_method.map((m) => (
                  <span key={m.method} className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    {m.method}: {money(m.total)}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div className="rounded-lg border border-gray-200 p-4 text-center dark:border-gray-700">
            <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Scan to give</h3>
            {station?.qr_image ? (
              <img src={station.qr_image} alt="Scan to donate" className="mx-auto h-40 w-40" />
            ) : (
              <div className="mx-auto flex h-40 w-40 items-center justify-center rounded border-2 border-dashed border-gray-300 text-xs text-gray-400">
                QR unavailable
              </div>
            )}
            {station?.donate_url ? (
              <a
                href={station.donate_url}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
              >
                {station.donate_url} <ExternalLink size={11} />
              </a>
            ) : null}
          </div>
        </div>

        <div className="space-y-4">
          <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Record a gift</h3>
            <div className="grid gap-2 sm:grid-cols-2">
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                type="number"
                min="0"
                placeholder="Amount"
                className={field}
              />
              <select value={method} onChange={(e) => setMethod(e.target.value)} className={field}>
                <option value="cash">Cash</option>
                <option value="check">Check</option>
                <option value="offline">Pledge</option>
                <option value="other">Other</option>
              </select>
              <input value={donor} onChange={(e) => setDonor(e.target.value)} placeholder="Donor name" className={field} />
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email (for the receipt)"
                className={field}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button disabled={busy || !amount} onClick={record}>
                {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Plus size={14} className="mr-1" />}
                Record gift
              </Button>
              <Button
                variant="outline"
                disabled={!amount || parseFloat(amount) <= 0 || !terminal?.ready}
                onClick={() => setShowReader(true)}
                title={terminal?.ready ? 'Take a card on the reader' : 'Card reader not configured'}
              >
                <CreditCard size={14} className="mr-1" /> Take a card
              </Button>
            </div>
            {msg ? <p className="text-xs text-gray-600 dark:text-gray-300">{msg}</p> : null}

            {!terminal?.ready ? (
              <div className="rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                <strong className="text-gray-800 dark:text-gray-200">Card reader not set up.</strong>{' '}
                {terminal?.enabled === false
                  ? 'Card payments are not enabled for the platform yet.'
                  : 'This organization has no connected Stripe account yet.'}{' '}
                Until then the QR above is the card path — donors scan it and gifts roll up here automatically.
              </div>
            ) : null}
          </section>

          <section className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Recent gifts</h3>
            {station?.recent_gifts?.length ? (
              <div className="max-h-64 divide-y overflow-y-auto dark:divide-gray-700">
                {station.recent_gifts.map((g) => (
                  <div key={g.id} className="flex items-center justify-between py-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm text-gray-900 dark:text-gray-100">{g.donor_name}</div>
                      <div className="text-xs text-gray-400">
                        {g.method}
                        {g.created_at ? ` · ${new Date(g.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''}
                      </div>
                    </div>
                    <span className="shrink-0 font-semibold text-green-700 dark:text-green-400">{money(g.amount)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="py-6 text-center text-sm text-gray-400">No gifts yet tonight.</p>
            )}
          </section>
        </div>
      </div>

      {showReader ? (
        <CardReaderPad
          eventId={event.id}
          amount={parseFloat(amount) || 0}
          donorName={donor.trim() || undefined}
          onClose={() => setShowReader(false)}
          onSuccess={() => {
            setAmount('');
            setDonor('');
            setEmail('');
            load();
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
};

export default EventGivingStation;
